import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@xantory/store';
import { api } from '../../core/client';
import { resolvePath } from '../../core/path';
import type { Attachment, AttachmentEntityType, AttachmentKind } from './attachments.types';

const BASE = '/v1/attachments';
const KEY = ['attachments'] as const;

export function useAttachments(entityType: AttachmentEntityType, entityId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, entityType, entityId],
    queryFn: () => api.get<Attachment[]>(`${BASE}/${entityType}/${entityId}`),
    enabled: !!entityId,
  });
}

/** Raw-body upload: the file bytes with their Content-Type + kind/name query
 *  (mirrors useUploadAssetDoc's bearer pattern — react-query's JSON client
 *  can't carry an arbitrary binary body). */
export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityType,
      entityId,
      kind,
      file,
    }: {
      entityType: AttachmentEntityType;
      entityId: string;
      kind: AttachmentKind;
      file: File;
    }) => {
      const { accessToken } = useAuthStore.getState();
      const url = resolvePath(
        `${BASE}/${entityType}/${entityId}?kind=${kind}&name=${encodeURIComponent(file.name)}`,
      );
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: file,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `upload failed (${res.status})`);
      }
      return (await res.json()) as { data: Attachment };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Authorized binary download; opens the object in a new tab. */
export async function openAttachment(
  entityType: AttachmentEntityType,
  entityId: string,
  id: string,
): Promise<void> {
  const { accessToken } = useAuthStore.getState();
  const res = await fetch(resolvePath(`${BASE}/${entityType}/${entityId}/${id}/download`), {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new Error(`download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useRemoveAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      id,
    }: {
      entityType: AttachmentEntityType;
      entityId: string;
      id: string;
    }) => api.delete<boolean>(`${BASE}/${entityType}/${entityId}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
